import React from 'react';
import { Link as LinkIcon, Trash, Eye, Copy } from '@phosphor-icons/react';
import { formatCurrency } from '../../utils/formatters';
import { VAT_RATE } from '../../utils/costCalculations';
import { STATUS_LABELS, STATUS_CLASSES, VALID_TRANSITIONS } from '../../constants/quoteStatus';
import styles from './QuoteRow.module.css';

/**
 * QuoteRow — shared row/card component for displaying a single vendor quote.
 *
 * Used by BOQQuotesTab, VendorsPage, and TradeQuotesModal.
 *
 * Props:
 * - quote: the vendor_quote object (id, status, total_price, created_at)
 * - vendorName: string
 * - priced: { pricedCount, totalItems } — optional "X/Y priced" badge
 * - totalOverride: number | null — overrides quote.total_price for display (e.g. sum of line items)
 * - showVATTotal: boolean — if true, total is shown incl. VAT (with pre-VAT subtext)
 * - selected: boolean — visual active/expanded state
 * - copied: boolean — renders Copy icon instead of Link for share feedback
 * - onClick: row click handler (main action, e.g. expand or open detail)
 * - onView: if provided, renders an Eye icon button that calls this on click
 * - onShare: if provided, renders a share icon button
 * - onStatusChange: (newStatus) => void — renders a status select dropdown if provided
 * - onDelete: if provided, renders a delete button
 * - allowDelete: boolean — when false, hides the delete button even if onDelete is passed (default true)
 */
const QuoteRow = ({
  quote,
  vendorName,
  priced,
  totalOverride = null,
  showVATTotal = false,
  selected = false,
  copied = false,
  onClick,
  onView,
  onShare,
  onStatusChange,
  onDelete,
  allowDelete = true,
}) => {
  const statusClass = STATUS_CLASSES[quote.status] || 'draft';
  const transitions = VALID_TRANSITIONS[quote.status] || [];
  const rawTotal = totalOverride != null ? totalOverride : quote.total_price;
  const hasPrice = rawTotal != null && rawTotal !== '';

  const formatDate = dateStr => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const stop = (e, fn) => {
    e.stopPropagation();
    if (fn) fn();
  };

  return (
    <div
      className={`${styles.row} ${selected ? styles.selected : ''} ${onClick ? styles.clickable : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={e => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {/* Main info */}
      <div className={styles.main}>
        <div className={styles.topLine}>
          <span className={styles.vendorName}>{vendorName || 'ספק'}</span>
          <span className={`${styles.statusBadge} ${styles[`status_${statusClass}`]}`}>
            {STATUS_LABELS[quote.status] || quote.status}
          </span>
        </div>
        {priced && priced.totalItems > 0 && (
          <div className={styles.pricingProgress}>
            {priced.pricedCount}/{priced.totalItems} פריטים תומחרו
          </div>
        )}
        <div className={styles.date}>{formatDate(quote.created_at)}</div>
      </div>

      {/* Price */}
      <div className={styles.priceBlock}>
        {hasPrice ? (
          showVATTotal ? (
            <>
              <span className={styles.totalPrice}>{formatCurrency(rawTotal * (1 + VAT_RATE))}</span>
              <span className={styles.priceSubtext}>{formatCurrency(rawTotal)} + מע״מ</span>
            </>
          ) : (
            <span className={styles.totalPrice}>{formatCurrency(rawTotal)}</span>
          )
        ) : (
          <span className={styles.pricePending}>ממתין לתמחור</span>
        )}
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        {onView && (
          <button
            className={styles.actionBtn}
            onClick={e => stop(e, onView)}
            title="צפה בהצעה"
            type="button"
          >
            <Eye size={18} />
          </button>
        )}
        {onShare && (
          <button
            className={styles.actionBtn}
            onClick={e => stop(e, onShare)}
            title="העתק קישור שיתוף"
            type="button"
          >
            {copied ? <Copy size={18} /> : <LinkIcon size={18} />}
          </button>
        )}
        {onStatusChange && (
          <select
            className={`${styles.statusSelect} ${styles[`statusSelect_${statusClass}`]}`}
            value={quote.status}
            onClick={e => e.stopPropagation()}
            onChange={e => {
              const newStatus = e.target.value;
              if (newStatus && newStatus !== quote.status) {
                onStatusChange(newStatus);
              }
            }}
          >
            {Object.entries(STATUS_LABELS).map(([value, label]) => {
              const isCurrent = value === quote.status;
              const isValidTransition = transitions.includes(value);
              return (
                <option key={value} value={value} disabled={!isCurrent && !isValidTransition}>
                  {label}
                </option>
              );
            })}
          </select>
        )}
        {onDelete && allowDelete && (
          <button
            className={`${styles.actionBtn} ${styles.deleteBtn}`}
            onClick={e => stop(e, onDelete)}
            title="מחק"
            type="button"
          >
            <Trash size={18} />
          </button>
        )}
      </div>
    </div>
  );
};

export default QuoteRow;
